import { rtkApi } from '@/shared/api/rtkApi';
import { ApiResponse } from '@/shared/types/common';

export type ForgotPasswordClientType = 'landing' | 'admin' | 'lms' | 'mobile';

export interface ForgotPasswordRequest {
  email: string;
  clientType?: ForgotPasswordClientType;
}

export interface ForgotPasswordResponse extends ApiResponse {
  message: string;
}

export interface ResetPasswordRequest {
  code: string;
  password: string;
}

export interface ResetPasswordResponse extends ApiResponse {
  message: string;
}

export type ResetTokenStatus = 'valid' | 'invalid' | 'expired' | 'blocked';

export interface CheckResetTokenResponse extends ApiResponse {
  message: string;
  data: { status: ResetTokenStatus };
}

export interface ResendStudentVerificationEmailRequest {
  email: string;
}

export interface ResendStudentVerificationEmailResponse extends ApiResponse {
  message: string;
}

export interface SetPasswordRequest {
  token: string;
  password: string;
}

export interface SetPasswordResponse extends ApiResponse {
  message: string;
  data: {
    jwt: string;
    user: {
      id: number;
      email: string;
      firstName: string;
      lastName: string;
    };
  };
}

export const authApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    forgotPassword: build.mutation<ForgotPasswordResponse, ForgotPasswordRequest>({
      query: (body) => ({
        url: '/auth/forgot',
        method: 'POST',
        body: {
          email: body.email,
          clientType: body.clientType ?? 'admin',
        },
      }),
    }),
    resetPassword: build.mutation<ResetPasswordResponse, ResetPasswordRequest>({
      query: (body) => ({
        url: '/auth/reset',
        method: 'POST',
        body: {
          code: body.code,
          password: body.password,
        },
      }),
    }),
    // Проверка ссылки до показа формы: токен не тратится, ответ всегда 200 со статусом.
    // POST /auth/reset/check бэкенд убрал (405) — с ним форма открывалась по подделанной ссылке
    checkResetToken: build.query<ResetTokenStatus, string>({
      query: (token) => ({
        url: '/auth/reset',
        params: { token },
      }),
      transformResponse: (response: CheckResetTokenResponse) => response.data.status,
    }),
    resendStudentVerificationEmail: build.mutation<
      ResendStudentVerificationEmailResponse,
      ResendStudentVerificationEmailRequest
    >({
      query: (body) => ({
        url: '/auth/student/resend-confirmation',
        method: 'POST',
        body: {
          email: body.email,
        },
      }),
    }),
    setPassword: build.mutation<SetPasswordResponse, SetPasswordRequest>({
      query: (body) => ({
        url: '/auth/set-password',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useCheckResetTokenQuery,
  useResendStudentVerificationEmailMutation,
  useSetPasswordMutation,
} = authApi;
